import { memo } from 'react';
import {
  analyticsSegmentTab,
  analyticsSegmentTabList,
  sicSegmentTab,
  sicSegmentTabList,
} from './segmentTabs.styles.js';

export interface SegmentTabItem {
  readonly id: string;
  readonly label: string;
  /** Optional count badge (e.g. catalog tab totals). */
  readonly count?: number;
}

export type SegmentTabsVariant = 'analytics' | 'sic';

export interface SegmentTabsProps {
  readonly tabs: readonly SegmentTabItem[];
  readonly activeId: string;
  readonly onChange: (id: string) => void;
  readonly ariaLabel?: string;
  readonly variant?: SegmentTabsVariant;
  /** Prefix for tab/panel ids (`{prefix}-tab-{id}` / `{prefix}-panel-{id}`). Analytics variant only. */
  readonly idPrefix?: string;
}

export const SegmentTabs = memo<SegmentTabsProps>(({
  tabs,
  activeId,
  onChange,
  ariaLabel,
  variant = 'analytics',
  idPrefix,
}) => {
  if (variant === 'sic') {
    return (
      <div className={sicSegmentTabList()} role="tablist" aria-label={ariaLabel}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={sicSegmentTab({ active: isActive })}
              onClick={() => onChange(tab.id)}
            >
              <span className="inline-flex items-center gap-1.5">
                {tab.label}
                {tab.count !== undefined ? (
                  <span
                    className={
                      isActive
                        ? 'inline-flex min-w-[1.25rem] items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-an-on-primary)_16%,transparent)] px-1.5 text-xs font-semibold tabular-nums text-[color:var(--color-an-on-primary)]'
                        : 'inline-flex min-w-[1.25rem] items-center justify-center rounded-md bg-[var(--color-an-surface-muted,var(--color-an-bg))] px-1.5 text-xs font-semibold tabular-nums text-[var(--color-an-text-muted)]'
                    }
                  >
                    {tab.count}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={analyticsSegmentTabList()}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        const tabId = idPrefix != null ? `${idPrefix}-tab-${tab.id}` : undefined;
        const panelId = idPrefix != null ? `${idPrefix}-panel-${tab.id}` : undefined;
        return (
          <button
            key={tab.id}
            id={tabId}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={isActive ? 0 : -1}
            className={analyticsSegmentTab({ active: isActive })}
            onClick={() => onChange(tab.id)}
          >
            <span className="inline-flex items-center gap-1.5">
              {tab.label}
              {tab.count !== undefined ? (
                <span
                  className={
                    isActive
                      ? 'inline-flex min-w-[1.25rem] items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-an-on-primary)_16%,transparent)] px-1.5 text-xs font-semibold tabular-nums text-[color:var(--color-an-on-primary)]'
                      : 'inline-flex min-w-[1.25rem] items-center justify-center rounded-md bg-[var(--color-an-surface-muted,var(--color-an-bg))] px-1.5 text-xs font-semibold tabular-nums text-[var(--color-an-text-muted)]'
                  }
                >
                  {tab.count}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
});

SegmentTabs.displayName = 'SegmentTabs';
